import {
  CircuitBoardIcon,
  ClipboardListIcon,
  CogIcon,
  CpuIcon,
  GaugeIcon,
  MonitorIcon,
  NetworkIcon,
  WindIcon,
  WrenchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  result: string;
  icon: LucideIcon;
  tags: string;
};

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de Automatización Industrial',
    subtitle: 'Comprender qué es la automatización en la industria',
    description:
      'Introducción a la automatización industrial, evolución de sistemas automatizados, automatización fija, programable y flexible, sistemas de control industrial, control de lazo abierto y cerrado, variables y señales industriales, arquitectura de sistemas automatizados, introducción a sensores, actuadores y controladores. Todo el contexto que necesitás para hablar con autoridad desde el primer día.',
    result:
      'Tus alumnos entienden por qué los sistemas se automatizan, qué diferencias existen entre tipos de automatización, cómo funcionan los lazos de control, cuáles son los componentes fundamentales, y por qué la automatización industrial es clave para la competitividad y la precisión en procesos modernos.',
    icon: CpuIcon,
    tags: 'AUTOMATIZACIÓN FIJA · PROGRAMABLE · FLEXIBLE · LAZO ABIERTO Y CERRADO · SEÑALES INDUSTRIALES · ARQUITECTURA DE CONTROL',
  },
  {
    number: '02',
    title: 'Sensores, Actuadores e Instrumentación Industrial',
    subtitle: 'Pasar de la teoría a identificar y seleccionar dispositivos reales',
    description:
      'Sensores industriales (inductivos, capacitivos, fotoeléctricos, ultrasónicos), sensores de temperatura, presión, nivel y caudal, señales analógicas y digitales, transmisores e instrumentación, actuadores eléctricos y neumáticos, selección y aplicación de dispositivos, conexionado e interpretación, diagnóstico de fallas. Con casos argentinos.',
    result:
      'Tus alumnos pueden identificar el sensor correcto para cada aplicación, entender cómo funcionan los transmisores, seleccionar actuadores adecuados, leer esquemas de conexionado, interpretar señales industriales y diagnosticar fallas en dispositivos. Comprenden que la sensorización es el primer paso de toda automatización.',
    icon: GaugeIcon,
    tags: 'INDUCTIVOS · CAPACITIVOS · FOTOELÉCTRICOS · ULTRASÓNICOS · TEMPERATURA Y PRESIÓN · TRANSMISORES · CONEXIONADO · DIAGNÓSTICO',
  },
  {
    number: '03',
    title: 'PLC: Programación y Control Industrial',
    subtitle: 'El corazón de los sistemas automatizados',
    description:
      'Arquitectura y funcionamiento del PLC, entradas y salidas, ciclo de ejecución, lógica de control, programación Ladder, contactos, bobinas y memorias, temporizadores y contadores, secuencias automáticas, interbloqueos y condiciones, diagnóstico de programas, ejercicios progresivos, aplicaciones industriales.',
    result:
      'Tus alumnos comprenden cómo funciona un PLC internamente, saben programar secuencias básicas en Ladder, pueden integrar temporizadores y contadores, diseñan interbloqueos de seguridad, diagnostican errores en programas y comprenden que la programación es el lenguaje del control automático.',
    icon: CircuitBoardIcon,
    tags: 'ENTRADAS Y SALIDAS · CICLO DE EJECUCIÓN · LADDER · CONTACTOS Y BOBINAS · TEMPORIZADORES · CONTADORES · INTERBLOQUEOS',
  },
  {
    number: '04',
    title: 'Electroneumática y Automatismos Industriales',
    subtitle: 'Lo esencial para talleres y espacios técnicos',
    description:
      'Fundamentos de neumática, preparación y distribución del aire, cilindros y actuadores, válvulas y electroválvulas, circuitos neumáticos, control electroneumático, sensores y finales de carrera, secuencias de movimiento, automatismos combinados, interpretación de esquemas, diagnóstico de fallas, prácticas para aula-taller.',
    result:
      'Tus alumnos diseñan circuitos neumáticos funcionales, comprenden cómo integrar electroválvulas con sensores, programan secuencias de movimiento, interpretan esquemas industriales, diagnostican fallas neumáticas y comprenden que la electroneumática es fundamental en máquinas industriales reales.',
    icon: WindIcon,
    tags: 'CILINDROS · ELECTROVÁLVULAS · CIRCUITOS NEUMÁTICOS · FINALES DE CARRERA · SECUENCIAS DE MOVIMIENTO · ESQUEMAS · AULA-TALLER',
  },
  {
    number: '05',
    title: 'Motores Eléctricos, Variadores y Control de Movimiento',
    subtitle: 'Accionamiento de máquinas y procesos',
    description:
      'Fundamentos de motores eléctricos, motores utilizados en industria, arranque y control, inversión de giro, protecciones, variadores de frecuencia (VFD), parámetros fundamentales de un variador, regulación de velocidad, rampas de aceleración y desaceleración, integración PLC-variador, aplicaciones industriales, diagnóstico de fallas.',
    result:
      'Tus alumnos entienden cómo funcionan los motores eléctricos industriales, saben configurar variadores de frecuencia, pueden programar rampas de velocidad, integran variadores con PLCs, diagnostican problemas en accionamientos y comprenden que el control de motores es fundamental para regular procesos.',
    icon: CogIcon,
    tags: 'ARRANQUE E INVERSIÓN DE GIRO · PROTECCIONES · VFD · PARÁMETROS · RAMPAS · INTEGRACIÓN PLC-VARIADOR · DIAGNÓSTICO',
  },
  {
    number: '06',
    title: 'Redes y Comunicaciones Industriales',
    subtitle: 'Conectar dispositivos en sistemas integrados',
    description:
      'Fundamentos de comunicaciones industriales, arquitecturas de red, comunicación entre PLC y dispositivos, Modbus, Ethernet industrial, PROFINET, PROFIBUS, direccionamiento y topologías, intercambio de información, integración PLC-HMI, diagnóstico básico de comunicaciones, casos de aplicación.',
    result:
      'Tus alumnos entienden cómo se comunican los dispositivos industriales, pueden configurar redes Modbus, conocen los principales protocolos industriales (PROFINET, PROFIBUS), integran PLCs con HMIs mediante comunicación, diagnostican problemas de red y comprenden que la interconexión es clave en la industria 4.0.',
    icon: NetworkIcon,
    tags: 'MODBUS · ETHERNET INDUSTRIAL · PROFINET · PROFIBUS · TOPOLOGÍAS · DIRECCIONAMIENTO · INTEGRACIÓN PLC-HMI',
  },
  {
    number: '07',
    title: 'HMI, SCADA y Supervisión de Procesos Industriales',
    subtitle: 'Visualizar y controlar procesos en tiempo real',
    description:
      'Fundamentos de HMI, diseño de interfaces, pantallas de operación, variables y tags, indicadores y controles, alarmas, tendencias, introducción a SCADA, supervisión de procesos, integración PLC-HMI-SCADA, diagnóstico mediante información de proceso, ejemplos industriales.',
    result:
      'Tus alumnos diseñan interfaces HMI funcionales, crean pantallas de operación intuitivas, configuran alarmas y tendencias, integran HMIs con PLCs, comprenden supervisión en tiempo real y aprenden que el HMI es el puente entre el operario y el sistema automatizado.',
    icon: MonitorIcon,
    tags: 'PANTALLAS DE OPERACIÓN · TAGS Y VARIABLES · ALARMAS · TENDENCIAS · SCADA · SUPERVISIÓN EN TIEMPO REAL',
  },
  {
    number: '08',
    title: '100 Prácticas y Desafíos de Automatización Industrial',
    subtitle: 'Aprender haciendo con ejercicios progresivos',
    description:
      '100 propuestas prácticas y desafíos progresivos para trabajar: sensores, actuadores, PLC, programación Ladder, electroneumática, motores, variadores, redes industriales, HMI y SCADA, secuencias automáticas, diagnóstico de fallas, integración de tecnologías.',
    result:
      'Tus alumnos practican con ejercicios reales de complejidad creciente, aplican conceptos inmediatamente, resuelven desafíos técnicos, transfieren lo aprendido a contextos industriales reales y desarrollan competencias prácticas verificables.',
    icon: WrenchIcon,
    tags: '100 PRÁCTICAS PROGRESIVAS · SECUENCIAS AUTOMÁTICAS · DIAGNÓSTICO DE FALLAS · INTEGRACIÓN DE TECNOLOGÍAS',
  },
  {
    number: '09',
    title: 'Planificación Docente, Secuencias y Proyectos de Automatización Industrial',
    subtitle: 'Herramientas para organizar la enseñanza de forma sistemática',
    description:
      'Organización de contenidos, planificación docente, secuencias didácticas completas, clases teórico-prácticas, actividades para aula-taller, aprendizaje basado en proyectos, estrategias de enseñanza, evaluación de competencias, integración entre teoría y práctica, proyectos de automatización, adaptación a diferentes contextos educativos.',
    result:
      'Tus alumnos participan en clases estructuradas, comprenden la lógica de la automatización desde el inicio, desarrollan proyectos significativos que integran múltiples tecnologías, transfieren lo aprendido a contextos profesionales reales.',
    icon: ClipboardListIcon,
    tags: 'SECUENCIAS DIDÁCTICAS · CLASES TEÓRICO-PRÁCTICAS · ABP · EVALUACIÓN DE COMPETENCIAS · PROYECTOS DE AUTOMATIZACIÓN',
  },
];
